import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchArtists } from '../services/artistService';
import { useAuth } from './AuthContext';
import { getFirestore, collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import app from '../config/firebase';

const db = getFirestore(app);
const ArtistsContext = createContext();

export const useArtists = () => {
  const context = useContext(ArtistsContext);
  if (!context) {
    throw new Error('useArtists must be used within an ArtistsProvider');
  }
  return context;
};

export const ArtistsProvider = ({ children }) => {
  const [artists, setArtists] = useState([]);
  const [invites, setInvites] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [loadingRegistrations, setLoadingRegistrations] = useState(true);
  const [error, setError] = useState(null);
  const [registrationError, setRegistrationError] = useState(null);
  
  const { user, isAdmin } = useAuth();

  const loadArtists = useCallback(async () => {
    if (!user || !isAdmin) {
      return;
    }

    setLoadingArtists(true);
    setError(null);
    
    try {
      const data = await fetchArtists();
      setArtists(data);
    } catch (err) {
      console.error('Failed to load artists:', err);
      setError(err.message);
    } finally {
      setLoadingArtists(false);
    }
  }, [user, isAdmin]);

  // Load artists once when user becomes admin
  useEffect(() => {
    if (user && isAdmin) {
      loadArtists();
    } else {
      setArtists([]);
      setError(null);
    }
  }, [user, isAdmin, loadArtists]);

  // Subscribe to invites real-time updates
  useEffect(() => {
    if (!user || !isAdmin) {
      setInvites([]);
      return;
    }

    const q = query(collection(db, 'invites'), orderBy('created_at', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedInvites = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || 'Unknown',
          email: data.email || '',
          emailTemplate: data.email_template || 'standard',
          requestedAt: data.created_at?.toDate() || new Date(),
        };
      });
      setInvites(fetchedInvites);
      setLoadingInvites(false);
    }, (error) => {
      console.error("Error fetching invites:", error);
      setLoadingInvites(false);
    });

    return () => unsubscribe();
  }, [user, isAdmin]);

  // Subscribe to direct artist registrations
  useEffect(() => {
    if (!user || !isAdmin) {
      setRegistrations([]);
      setLoadingRegistrations(false);
      setRegistrationError(null);
      return;
    }

    setLoadingRegistrations(true);
    setRegistrationError(null);
    const q = query(collection(db, 'artist_registrations'), orderBy('created_at', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedRegistrations = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          uid: data.uid || doc.id,
          email: data.email || '',
          country: data.country || '',
          city: data.city || '',
          portfolioUrl: data.portfolio_url || data.portfolioUrl || '',
          registeredBusiness: data.registered_business === true || data.registeredBusiness === true,
          status: data.status || 'pending',
          source: data.source || 'artist_join',
          requestedAt: data.created_at?.toDate() || null,
          updatedAt: data.updated_at?.toDate() || null,
        };
      });
      setRegistrations(fetchedRegistrations);
      setRegistrationError(null);
      setLoadingRegistrations(false);
    }, (error) => {
      console.error("Error fetching artist registrations:", error);
      setRegistrations([]);
      setRegistrationError('Unable to load artist registration requests.');
      setLoadingRegistrations(false);
    });

    return () => unsubscribe();
  }, [user, isAdmin]);

  const refreshArtists = async () => {
    await loadArtists();
  };

  const value = {
    artists,
    invites,
    registrations,
    loadingArtists,
    loadingInvites,
    loadingRegistrations,
    error,
    registrationError,
    refreshArtists,
  };

  return (
    <ArtistsContext.Provider value={value}>
      {children}
    </ArtistsContext.Provider>
  );
};
